import { portalForAccessRole, type AccessRole } from '@services/access';
import { isUnderBasePath, PORTALS } from './portals';

export const RETURN_PATH_KEY = 'postLoginReturn';
/** Longest a remembered path stays valid across the sign-in round trip. */
export const RETURN_PATH_TTL_MS = 30 * 60 * 1000;

interface StoredReturn {
  path: string;
  at: number;
}

const UNSAFE_PATH = /[\\\s]|\/\/|\.\.|%2e|%5c|%2f/i;
const LAST_CONTROL_CHAR = 0x1f;

const hasControlChar = (value: string): boolean =>
  [...value].some((char) => char.charCodeAt(0) <= LAST_CONTROL_CHAR);

function portalBasePaths(): string[] {
  return Object.values(PORTALS).map((portal) => portal.basePath);
}

/** True for a same-origin path (pathname + search) inside a portal; never /auth. */
export function isRememberablePath(path: string): boolean {
  if (!path.startsWith('/') || UNSAFE_PATH.test(path) || hasControlChar(path)) {
    return false;
  }
  const pathname = path.split(/[?#]/, 1)[0];
  return portalBasePaths().some((base) => isUnderBasePath(pathname, base));
}

/** Stores the page a signed-out visitor wanted, so login can return there. */
export function rememberReturnPath(path: string, now: number = Date.now()): void {
  if (!isRememberablePath(path)) return;
  try {
    const entry: StoredReturn = { path, at: now };
    sessionStorage.setItem(RETURN_PATH_KEY, JSON.stringify(entry));
  } catch {
    // Storage unavailable: login lands on the role home as before.
  }
}

/** Drops any remembered path so it cannot leak into a later sign-in. */
export function discardReturnPath(): void {
  try {
    sessionStorage.removeItem(RETURN_PATH_KEY);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}

/** Reads and clears the remembered path; null unless it is fresh and the role may enter it. */
export function consumeReturnPath(role: AccessRole, now: number = Date.now()): string | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(RETURN_PATH_KEY);
  } catch {
    return null;
  }
  discardReturnPath();
  if (!raw) return null;

  let entry: Partial<StoredReturn>;
  try {
    entry = JSON.parse(raw) as Partial<StoredReturn>;
  } catch {
    return null;
  }
  const { path, at } = entry;
  if (typeof path !== 'string' || typeof at !== 'number') return null;
  if (now - at > RETURN_PATH_TTL_MS || now < at) return null;
  if (!isRememberablePath(path)) return null;

  const pathname = path.split(/[?#]/, 1)[0];
  const portal = PORTALS[portalForAccessRole(role)];
  return isUnderBasePath(pathname, portal.basePath) ? path : null;
}
